import { DataScienceEnums } from 'dashboard-services/api/dataScience'

import Papa from 'papaparse'

import SqlScannerService from 'services/SqlScannerService'

export default (() => {
  const VALUE_TYPES = DataScienceEnums.TASK_PARAM_VALUE_TYPE
  const MULTI_VALUE_TYPES = [
    VALUE_TYPES.MULTI_SELECT,
    VALUE_TYPES.MULTI_SYMBOL_COLUMN,
    VALUE_TYPES.MULTI_SYMBOL_KEY,
    VALUE_TYPES.MULTI_SYMBOL_VALUE,
    VALUE_TYPES.MULTI_METADATA_KEY
  ]
  const SQL_EXCLUDED_VALUE_TYPES = [
    VALUE_TYPES.DATALAKE_FILE,
    VALUE_TYPES.DATA_PREP_PARAM,
    VALUE_TYPES.FILE,
    VALUE_TYPES.PIPELINE,
    VALUE_TYPES.SYMBOL,
    VALUE_TYPES.SYMBOL_FILTER,
    VALUE_TYPES.MULTI_SYMBOL_FILTER,
    VALUE_TYPES.METADATA_FILTER,
    VALUE_TYPES.MULTI_METADATA_FILTER,
    VALUE_TYPES.JSON,
    VALUE_TYPES.BOOLEAN_PAUSE,
    VALUE_TYPES.WAREHOUSE_USER_REQUIRED
  ]
  const GROUP_DEPENDENT_TYPES = [
    VALUE_TYPES.SYMBOL_COLUMN,
    VALUE_TYPES.MULTI_SYMBOL_COLUMN,
    VALUE_TYPES.SYMBOL_KEY,
    VALUE_TYPES.MULTI_SYMBOL_KEY,
    VALUE_TYPES.METADATA_KEY,
    VALUE_TYPES.MULTI_METADATA_KEY
  ]
  const KEY_DEPENDENT_TYPES = [VALUE_TYPES.SYMBOL_VALUE, VALUE_TYPES.MULTI_SYMBOL_VALUE]
  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)
  const toArray = ({ value, splitString = true }) => {
    if(Array.isArray(value)) {
      return value
    }
    if(value === undefined || value === null || value === '') {
      return []
    }
    if(!splitString) {
      return [value]
    }
    return String(value).split(',').map(item => item.trim())
  }
  const isBindingMultiple = binding => binding.multiple || MULTI_VALUE_TYPES.includes(binding.valueType)
  const isScalarValuePresent = value => value === 0 || typeof value === 'boolean' || (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value)) || (value !== undefined && value !== null && typeof value === 'object' && Object.keys(value).length > 0)
  const isValuePresent = value => Array.isArray(value) ? value.length > 0 && value.every(isScalarValuePresent) : isScalarValuePresent(value)
  const normalizeBindingValue = (binding, value) => {
    if(value === undefined || value === null) {
      return value
    }

    if(binding.valueType === VALUE_TYPES.NUMBER && typeof value === 'string' && value.trim() !== '') {
      const numberValue = Number(value)
      return Number.isFinite(numberValue) ? numberValue : value
    }

    if(!isBindingMultiple(binding) || Array.isArray(value)) {
      return value
    }

    return value === '' ? [] : String(value).split(',').map(item => item.trim())
  }

  const getTemplateReferenceStep = ({ sql, index }) => {
    let braceDepth = 1,
        next = index + 2

    while(next < sql.length && braceDepth) {
      if(sql[next] === '{') {
        braceDepth++
      } else if(sql[next] === '}') {
        braceDepth--
      }
      next++
    }

    return { next }
  }

  const getParameterStep = ({ sql, index }) => {
    const nextChar = sql[index + 1],
          parameter = nextChar !== ':' && nextChar !== '=' && sql[index - 1] !== ':'
            ? sql.slice(index + 1).match(/^[A-Za-z_][A-Za-z0-9_]*/)?.[0]
            : undefined,
          end = index + (parameter?.length || 0) + 1

    return parameter
      ? { token: { name: parameter, start: index, end }, next: end }
      : { next: index + 1 }
  }

  const getNormalStep = ({ sql, index }) => {
    const char = sql[index],
          nextChar = sql[index + 1]

    if(char === '$' && nextChar === '{') {
      return getTemplateReferenceStep({ sql, index })
    }
    if(char === ':') {
      return getParameterStep({ sql, index })
    }
    return { next: index + 1 }
  }

  const getParameterTokens = ({ sql = '' }) => SqlScannerService.scan({ sql, getNormalStep })

  const getParameterNames = ({ sql = "" }) => Array.from(new Set(getParameterTokens({ sql }).map(parameter => parameter.name)))
  const isParameterNameValid = ({ name }) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(name || '')
  const getUniqueParameterName = ({ names = [], baseName = 'parameter' }) => {
    const existingNames = new Set(names)
    let index = 1,
        name = baseName

    while(existingNames.has(name)) {
      index++
      name = `${baseName}${index}`
    }

    return name
  }
  const updateParameterNames = ({ sql = '', replacements = {} }) => getParameterTokens({ sql }).reduceRight((updatedSql, parameter) => {
    if(!hasOwn(replacements, parameter.name)) {
      return updatedSql
    }

    const nextName = replacements[parameter.name],
          replacement = nextName ? `:${nextName}` : ''
    return `${updatedSql.slice(0, parameter.start)}${replacement}${updatedSql.slice(parameter.end)}`
  }, sql)

  const getNormalizedBinding = ({ binding, name }) => {
    if(binding) {
      const normalizedBinding = { ...binding }
      Reflect.deleteProperty(normalizedBinding, 'dependsOn')
      Reflect.deleteProperty(normalizedBinding, 'label')
      if(isBindingMultiple(normalizedBinding)) {
        return { ...normalizedBinding, defaultValue: normalizeBindingValue(normalizedBinding, normalizedBinding.defaultValue), multiple: true }
      }
      return normalizedBinding
    }
    return {
      name,
      valueType: VALUE_TYPES.STRING,
      defaultValue: '',
      options: [],
      multiple: false
    }
  }
  const reconcileBindings = ({ sql, bindings = [] }) => getParameterNames({ sql }).map(name => getNormalizedBinding({
    binding: bindings.find(candidate => candidate.name === name),
    name
  }))
  const mergeBindings = ({ sql, bindings = [] }) => {
    const bindingNames = new Set(bindings.map(binding => binding.name)),
          explicitBindings = bindings.map(binding => getNormalizedBinding({ binding, name: binding.name })),
          inferredBindings = reconcileBindings({ sql, bindings }).filter(binding => !bindingNames.has(binding.name))
    return [...explicitBindings, ...inferredBindings]
  }

  const getBindingValueTypes = ({ valueTypes = [] }) => valueTypes.filter(valueType => {
    const value = typeof valueType === 'string' ? valueType : valueType.value
    return !SQL_EXCLUDED_VALUE_TYPES.includes(value)
      && !GROUP_DEPENDENT_TYPES.includes(value)
      && !KEY_DEPENDENT_TYPES.includes(value)
  })

  const resolveBindingValues = ({ bindings = [], overrides = {}, currentSelection = {}, personalDefaults = {} }) => Object.fromEntries(
    bindings.map(binding => {
      const { name } = binding,
            value = hasOwn(overrides, name) ? overrides[name]
              : hasOwn(currentSelection, name) ? currentSelection[name]
                : hasOwn(personalDefaults, name) ? personalDefaults[name]
                  : binding.defaultValue
      return [name, normalizeBindingValue(binding, value)]
    })
  )

  const applyBindingValueChange = ({ name, value, values = {} }) => ({ ...values, [name]: value })

  const getErrorMessage = ({ error } = {}) => error?.message || 'Could not get SQL data.'

  const isBindingValueValid = ({ binding, value }) => binding.valueType === VALUE_TYPES.NUMBER
    ? typeof value === 'number' && Number.isFinite(value)
    : isValuePresent(value)

  const areBindingsComplete = ({ bindings = [], values = {} }) => bindings.every(binding => {
    const value = values[binding.name],
          isMultiple = isBindingMultiple(binding)
    return isBindingValueValid({ binding, value })
      && (isMultiple ? Array.isArray(value) : !Array.isArray(value))
  })

  const parseCsv = ({ csv, maxRows }) => {
    if (typeof csv !== 'string' || csv.trim().length === 0) {
      throw new Error('No data has been received.')
    }

    const result = Papa.parse(csv, {
      header: true,
      delimiter: ',',
      skipEmptyLines: true,
      dynamicTyping: false,
      ...(maxRows === undefined ? {} : { preview: maxRows })
    })
    const columns = result.meta.fields

    if (!columns || columns.length === 0) {
      throw new Error('No data has been received.')
    }

    if (result.errors.length > 0) {
      throw new Error('CSV file can not be parsed.')
    }

    return { columns, data: result.data }
  }

  return { applyBindingValueChange, areBindingsComplete, getBindingValueTypes, getErrorMessage, getParameterNames, getUniqueParameterName, isBindingMultiple, isParameterNameValid, mergeBindings, parseCsv, reconcileBindings, resolveBindingValues, toArray, updateParameterNames }
})()
